import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Trash2, Shield, LogOut, Plus, CheckCircle2, XCircle, Settings as SettingsIcon, KeyRound, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { LicenseStatusCard } from '../components/LicenseStatusCard';
import { UpdateStatusCard } from '../components/UpdateStatusCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'sf_master_token';

const fmtBRL = (n) => `R$ ${Number(n || 0).toFixed(2).replace('.', ',')}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('pt-BR') : '—');

const masterClient = (token) =>
  axios.create({
    baseURL: API,
    headers: { 'X-Master-Token': token },
  });

const LoginScreen = ({ onLogged }) => {
  const [password, setPassword] = useState('');
  const [firstTime, setFirstTime] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/billing/master/login`, { password });
      if (data.first_time) {
        setFirstTime(data);
        return;
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogged(data.token);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao entrar');
    } finally {
      setLoading(false);
    }
  };

  if (firstTime) {
    return (
      <Card className="p-6 max-w-md w-full" data-testid="master-first-time-card">
        <div className="flex items-center gap-2 text-amber-600 mb-3">
          <AlertTriangle /> <h2 className="text-lg font-bold">Primeiro acesso</h2>
        </div>
        <p className="text-sm text-slate-700 mb-2">
          {firstTime.message}
        </p>
        <div className="bg-slate-100 p-3 rounded font-mono text-lg text-center mb-3" data-testid="master-initial-password">
          {firstTime.initial_password}
        </div>
        <p className="text-xs text-amber-700 mb-4">
          Anote essa senha em local seguro. Você só vai vê-la AGORA.
        </p>
        <Button onClick={() => { setFirstTime(null); setPassword(''); }} className="w-full" data-testid="master-first-time-continue">
          Entendi, continuar
        </Button>
      </Card>
    );
  }

  return (
    <Card className="p-6 max-w-md w-full" data-testid="master-login-card">
      <div className="flex items-center gap-2 mb-4">
        <Shield className="text-indigo-600" /> <h2 className="text-lg font-bold">Painel Master</h2>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Acesso restrito ao administrador do sistema (proprietário do deploy).
      </p>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label>Senha mestra</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="master-password-input"
            autoFocus
            required
          />
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="master-login-btn">
          {loading ? 'Entrando...' : 'Entrar'}
        </Button>
      </form>
    </Card>
  );
};

const ChangePasswordModal = ({ token, onClose }) => {
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const save = async () => {
    if (newPw.length < 6) return toast.error('Mínimo 6 caracteres');
    if (newPw !== confirm) return toast.error('Senhas não conferem');
    try {
      await masterClient(token).post('/billing/master/change-password', { new_password: newPw });
      toast.success('Senha alterada. Faça login novamente.');
      localStorage.removeItem(TOKEN_KEY);
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Alterar senha mestra</DialogTitle>
          <DialogDescription>Defina uma nova senha (mínimo 6 caracteres). Sessões ativas serão invalidadas.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Nova senha</Label>
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="master-newpw-input" />
          </div>
          <div>
            <Label>Confirmar</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="master-newpw-confirm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} data-testid="master-save-newpw">Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SettingsModal = ({ token, current, onClose, onSaved }) => {
  const [form, setForm] = useState({
    amount: current?.amount ?? 99.90,
    pix_key: current?.pix_key || '',
    pix_key_type: current?.pix_key_type || 'telefone',
    pix_name: current?.pix_name || '',
    pix_city: current?.pix_city || '',
    tolerance_days: current?.tolerance_days ?? 2,
  });
  const save = async () => {
    try {
      await masterClient(token).post('/billing/master/settings', {
        amount: parseFloat(form.amount),
        pix_key: form.pix_key,
        pix_key_type: form.pix_key_type,
        pix_name: form.pix_name,
        pix_city: form.pix_city,
        tolerance_days: parseInt(form.tolerance_days),
      });
      toast.success('Configurações salvas');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Configurações da assinatura</DialogTitle>
          <DialogDescription>Valor, chave PIX e tolerância em dias após o vencimento.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Valor mensal (R$)</Label>
            <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="master-set-amount" />
          </div>
          <div>
            <Label>Tolerância (dias)</Label>
            <Input type="number" value={form.tolerance_days} onChange={(e) => setForm({ ...form, tolerance_days: e.target.value })} data-testid="master-set-tolerance" />
          </div>
          <div className="col-span-2">
            <Label>Chave PIX</Label>
            <Input value={form.pix_key} onChange={(e) => setForm({ ...form, pix_key: e.target.value })} placeholder="email/cpf/cnpj/telefone/aleatória" data-testid="master-set-pix-key" />
          </div>
          <div>
            <Label>Tipo</Label>
            <select className="w-full border rounded h-9 px-2" value={form.pix_key_type} onChange={(e) => setForm({ ...form, pix_key_type: e.target.value })} data-testid="master-set-pix-type">
              <option value="cpf">CPF</option>
              <option value="cnpj">CNPJ</option>
              <option value="telefone">Telefone</option>
              <option value="email">Email</option>
              <option value="aleatoria">Aleatória</option>
            </select>
          </div>
          <div>
            <Label>Nome do beneficiário</Label>
            <Input value={form.pix_name} onChange={(e) => setForm({ ...form, pix_name: e.target.value })} maxLength={25} data-testid="master-set-pix-name" />
          </div>
          <div className="col-span-2">
            <Label>Cidade</Label>
            <Input value={form.pix_city} onChange={(e) => setForm({ ...form, pix_city: e.target.value })} maxLength={15} data-testid="master-set-pix-city" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} data-testid="master-save-settings">Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CreatePeriodModal = ({ token, defaultAmount, onClose, onCreated }) => {
  const [days, setDays] = useState(30);
  const [amount, setAmount] = useState(defaultAmount || 99.90);
  const [date, setDate] = useState('');

  const submit = async () => {
    try {
      const body = { days: parseInt(days), amount: parseFloat(amount) };
      if (date) body.period_start = new Date(date).toISOString();
      await masterClient(token).post('/billing/master/create-period', body);
      toast.success('Período criado');
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Novo período de liberação</DialogTitle><DialogDescription>Cria um novo ciclo de cobrança. A data de vencimento será a soma do início + duração.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Início (opcional, padrão = hoje)</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="master-period-start" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Duração (dias)</Label>
              <Input type="number" value={days} onChange={(e) => setDays(e.target.value)} data-testid="master-period-days" />
            </div>
            <div>
              <Label>Valor (R$)</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="master-period-amount" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} data-testid="master-create-period-confirm">Criar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MarkPaidModal = ({ token, sub, onClose, onDone }) => {
  const [method, setMethod] = useState('manual_pix');
  const [notes, setNotes] = useState('');
  const submit = async () => {
    try {
      await masterClient(token).post(`/billing/master/${sub.id}/mark-paid`, { payment_method: method, notes });
      toast.success('Pagamento confirmado');
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Confirmar pagamento</DialogTitle><DialogDescription>Marca a liberação selecionada como paga e libera o sistema.</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>Período: <strong>{fmtDate(sub.period_start)} → {fmtDate(sub.period_end)}</strong></div>
          <div>Valor: <strong>{fmtBRL(sub.amount)}</strong></div>
          <div>
            <Label>Forma</Label>
            <select className="w-full border rounded h-9 px-2" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="master-paid-method">
              <option value="manual_pix">PIX manual</option>
              <option value="dinheiro">Dinheiro</option>
              <option value="transferencia">Transferência</option>
              <option value="boleto">Boleto</option>
              <option value="outro">Outro</option>
            </select>
          </div>
          <div>
            <Label>Observações</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="master-paid-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} data-testid="master-paid-confirm">Confirmar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Dashboard = ({ token, onLogout }) => {
  const [data, setData] = useState(null);
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [markPaid, setMarkPaid] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [dashRes, licRes, updRes] = await Promise.all([
        masterClient(token).get('/billing/master/dashboard'),
        axios.get(`${API}/license/status`).catch(() => ({ data: null })),
        axios.get(`${API}/updates/status`).catch(() => ({ data: null })),
      ]);
      setData(dashRes.data);
      setLicenseStatus(licRes.data);
      setUpdateStatus(updRes.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        onLogout();
        return;
      }
      toast.error('Erro ao carregar painel');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancel = async (id) => {
    if (!window.confirm('Reverter pagamento?')) return;
    try {
      await masterClient(token).post(`/billing/master/${id}/cancel-payment`);
      toast.success('Pagamento revertido');
      load();
    } catch (e) {
      toast.error('Erro');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Apagar definitivamente este período? Esta ação não pode ser desfeita.')) return;
    try {
      await masterClient(token).delete(`/billing/master/${id}`);
      toast.success('Período removido');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    onLogout();
  };

  if (loading) return <div className="p-8 text-center">Carregando...</div>;
  if (!data) return null;

  const status = data.status || {};
  const cur = status.subscription;
  const settings = status.settings || {};

  return (
    <div className="max-w-6xl w-full space-y-4" data-testid="master-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Shield className="text-indigo-600" />
          <h1 className="text-2xl font-bold">Painel Master · Smart Flow</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowChangePw(true)} data-testid="master-change-pw-btn">
            <KeyRound size={14} className="mr-1" /> Alterar senha
          </Button>
          <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="master-open-settings-btn">
            <SettingsIcon size={14} className="mr-1" /> Configurações
          </Button>
          <Button variant="outline" onClick={logout} data-testid="master-logout-btn">
            <LogOut size={14} className="mr-1" /> Sair
          </Button>
        </div>
      </div>

      <LicenseStatusCard licenseStatus={licenseStatus} fmtDate={fmtDate} />

      <UpdateStatusCard updateStatus={updateStatus} fmtDate={fmtDate} />

      {/* Status atual */}
      <Card className="p-4" data-testid="master-status-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-lg font-semibold">Liberação atual</div>
          {status.status === 'paid' ? (
            <Badge className="bg-emerald-600 text-white">Em dia</Badge>
          ) : status.overdue_days > 0 ? (
            <Badge className="bg-red-600 text-white">Vencido {status.overdue_days}d</Badge>
          ) : status.status === 'pending' ? (
            <Badge className="bg-amber-500 text-white">Vence em {status.days_to_due}d</Badge>
          ) : (
            <Badge variant="outline">Sem liberação</Badge>
          )}
        </div>
        {cur ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
            <div><div className="text-xs text-slate-500">Valor</div><div className="font-bold">{fmtBRL(cur.amount)}</div></div>
            <div><div className="text-xs text-slate-500">Período</div><div>{fmtDate(cur.period_start)} → {fmtDate(cur.period_end)}</div></div>
            <div><div className="text-xs text-slate-500">Vence</div><div>{fmtDate(cur.due_date)}</div></div>
            <div><div className="text-xs text-slate-500">Tolerância</div><div>{settings.tolerance_days || 2} dias</div></div>
          </div>
        ) : (
          <div className="text-sm text-slate-500 mt-2">
            Nenhuma liberação ativa. Crie um período abaixo.
          </div>
        )}
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => setShowCreate(true)} data-testid="master-create-period-btn">
          <Plus size={14} className="mr-1" /> Novo período
        </Button>
      </div>

      {/* Tabela histórico */}
      <Card className="p-4">
        <div className="font-semibold mb-3">Liberações</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b">
                <th className="py-2">Período</th>
                <th className="py-2">Vencimento</th>
                <th className="py-2">Valor</th>
                <th className="py-2">Status</th>
                <th className="py-2">Pago em</th>
                <th className="py-2">Forma</th>
                <th className="py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {data.subscriptions.map((s) => (
                <tr key={s.id} className="border-b" data-testid="master-sub-row">
                  <td className="py-2">{fmtDate(s.period_start)} → {fmtDate(s.period_end)}</td>
                  <td className="py-2">{fmtDate(s.due_date)}</td>
                  <td className="py-2">{fmtBRL(s.amount)}</td>
                  <td className="py-2">
                    {s.status === 'paid' ? (
                      <Badge className="bg-emerald-600 text-white">Pago</Badge>
                    ) : (
                      <Badge variant="outline">{s.status}</Badge>
                    )}
                  </td>
                  <td className="py-2">{fmtDate(s.paid_at)}</td>
                  <td className="py-2">{s.payment_method || '—'}</td>
                  <td className="py-2 text-right">
                    <div className="flex gap-1 justify-end">
                      {s.status !== 'paid' ? (
                        <Button size="sm" onClick={() => setMarkPaid(s)} data-testid={`master-mark-paid-${s.id}`}>
                          <CheckCircle2 size={14} className="mr-1" /> Marcar pago
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => cancel(s.id)} data-testid={`master-cancel-paid-${s.id}`}>
                          <XCircle size={14} className="mr-1" /> Reverter
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => remove(s.id)} data-testid={`master-delete-${s.id}`} title="Apagar período">
                        <Trash2 size={14} className="text-red-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {data.subscriptions.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-slate-500">Nenhum período registrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {showSettings && (
        <SettingsModal token={token} current={settings} onClose={() => setShowSettings(false)} onSaved={load} />
      )}
      {showCreate && (
        <CreatePeriodModal token={token} defaultAmount={settings.amount} onClose={() => setShowCreate(false)} onCreated={load} />
      )}
      {showChangePw && (
        <ChangePasswordModal token={token} onClose={() => setShowChangePw(false)} />
      )}
      {markPaid && (
        <MarkPaidModal token={token} sub={markPaid} onClose={() => setMarkPaid(null)} onDone={load} />
      )}
    </div>
  );
};

const MasterPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6" data-testid="master-page">
      {token ? (
        <Dashboard token={token} onLogout={() => setToken(null)} />
      ) : (
        <LoginScreen onLogged={setToken} />
      )}
    </div>
  );
};

export default MasterPage;
